import React, { useState, useEffect } from 'react';
import { MagnifyingGlassIcon, XMarkIcon, PlusIcon, TrashIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';

interface Size {
  name: string;
  stock_quantity: number;
  price?: number | null;
}
interface Variant {
  id: number;
  color: string;
  color_code?: string | null;
  price?: number | null;
  is_active: boolean;
  sizes: Size[];
}
interface Product {
  id: number;
  name: string;
  product_code: string;
  price: number;
  promotional_price?: number | null;
  product_images?: string[];
  variants: Variant[];
}

export interface EditorLine {
  key: string; // variant_id + size
  variant_id: number;
  product_name: string;
  color: string;
  size: string;
  unit_price: number;
  quantity: number;
  max_stock: number; // remaining stock available to ADD (existing qty already deducted)
}

interface Props {
  // existing order items → converted to lines by the parent
  value: EditorLine[];
  onChange: (lines: EditorLine[]) => void;
}

const effectivePrice = (p: Product, v: Variant, s: Size): number => {
  const base = s.price ?? v.price ?? p.price;
  const promo = p.promotional_price;
  return promo && promo < base ? promo : base;
};

const getImageUrl = (image?: string | null) => {
  if (!image) return null;
  if (image.startsWith('http')) return image;
  const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || '';
  return `${baseUrl}/storage/${image}`;
};

const ManualOrderItemsEditor: React.FC<Props> = ({ value, onChange }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<Variant | null>(null);

  useEffect(() => {
    if (!pickerOpen) return;
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await apiService.get('/admin/products', { params: { search, per_page: 30 } });
        if (!cancelled) setProducts(res.data || res.products || res || []);
      } catch {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [pickerOpen, search]);

  const openPicker = () => {
    setSelectedProduct(null);
    setSelectedVariant(null);
    setLoading(true);
    setPickerOpen(true);
  };

  const addLine = (product: Product, variant: Variant, size: Size) => {
    const key = `${variant.id}-${size.name}`;
    const existing = value.find((l) => l.key === key);
    if (existing) {
      // bump qty (allow up to existing qty + remaining stock)
      onChange(
        value.map((l) =>
          l.key === key ? { ...l, quantity: l.quantity + 1 } : l
        )
      );
    } else {
      onChange([
        ...value,
        {
          key,
          variant_id: variant.id,
          product_name: product.name,
          color: variant.color,
          size: size.name,
          unit_price: effectivePrice(product, variant, size),
          quantity: 1,
          max_stock: size.stock_quantity,
        },
      ]);
    }
    setSelectedVariant(null);
  };

  const setQty = (key: string, qty: number) => {
    onChange(value.map((l) => (l.key === key ? { ...l, quantity: Math.max(1, qty) } : l)));
  };
  const removeLine = (key: string) => onChange(value.filter((l) => l.key !== key));

  const subtotal = value.reduce((s, l) => s + l.unit_price * l.quantity, 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-semibold text-gray-700">Items</label>
        <button
          type="button"
          onClick={openPicker}
          className="inline-flex items-center gap-1 bg-indigo-600 text-white px-2.5 py-1 text-xs rounded-md hover:bg-indigo-700"
        >
          <PlusIcon className="h-4 w-4" />
          Add product
        </button>
      </div>

      {value.length === 0 ? (
        <p className="text-sm text-gray-500 py-3 text-center border border-dashed rounded-md">
          No items. Add at least one product.
        </p>
      ) : (
        <div className="border rounded-md divide-y divide-gray-100">
          {value.map((l) => (
            <div key={l.key} className="flex items-center gap-2 p-2.5">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{l.product_name}</p>
                <p className="text-xs text-gray-500">
                  {l.color} / {l.size} · {l.unit_price.toLocaleString()} MMK
                </p>
              </div>
              <input
                type="number"
                min={1}
                value={l.quantity}
                onChange={(e) => setQty(l.key, Number(e.target.value))}
                className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center"
              />
              <div className="w-24 text-right text-sm font-medium text-gray-900">
                {(l.unit_price * l.quantity).toLocaleString()}
              </div>
              <button
                type="button"
                onClick={() => removeLine(l.key)}
                className="text-gray-400 hover:text-red-600"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          ))}
          <div className="flex justify-between p-2.5 text-sm font-semibold bg-gray-50">
            <span>Items subtotal</span>
            <span>{subtotal.toLocaleString()} MMK</span>
          </div>
        </div>
      )}

      {/* Picker modal */}
      {pickerOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg w-full max-w-lg max-h-[85vh] flex flex-col shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-base font-bold text-gray-900">
                {selectedProduct ? selectedProduct.name : 'Select a product'}
              </h3>
              <button type="button" onClick={() => setPickerOpen(false)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto">
              {!selectedProduct ? (
                <>
                  <div className="relative mb-4">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                    <input
                      type="text"
                      autoFocus
                      placeholder="Search products..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div className="min-h-[280px]">
                    {loading ? (
                      <div className="flex items-center justify-center py-14 text-gray-500">
                        <svg className="animate-spin h-5 w-5 mr-2 text-indigo-500" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                        </svg>
                        Loading products...
                      </div>
                    ) : products.length === 0 ? (
                      <p className="text-center text-gray-500 py-14">No products found.</p>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {products.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => { setSelectedProduct(p); setSelectedVariant(p.variants?.[0] ?? null); }}
                            className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-gray-50 px-2 rounded"
                          >
                            {p.product_images?.[0] && (
                              <img src={getImageUrl(p.product_images[0]) || undefined} className="w-9 h-9 rounded object-cover" alt="" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                              <p className="text-xs text-gray-500 font-mono">{p.product_code}</p>
                            </div>
                            <span className="text-sm text-gray-700">{Number(p.price).toLocaleString()}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => { setSelectedProduct(null); setSelectedVariant(null); }}
                    className="text-sm text-indigo-600 hover:text-indigo-800 mb-4 inline-flex items-center gap-1"
                  >
                    <ArrowLeftIcon className="h-4 w-4" />
                    Back to products
                  </button>

                  <p className="text-xs font-medium text-gray-500 uppercase mb-2">Color</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {selectedProduct.variants.filter((v) => v.is_active).map((v) => (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => setSelectedVariant(v)}
                        className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2 border ${
                          selectedVariant?.id === v.id
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {v.color_code && (
                          <span className="w-4 h-4 rounded-full border border-gray-300" style={{ backgroundColor: v.color_code }} />
                        )}
                        {v.color}
                      </button>
                    ))}
                  </div>

                  {selectedVariant && (
                    <>
                      <p className="text-xs font-medium text-gray-500 uppercase mb-2">Size &amp; stock</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {selectedVariant.sizes.map((s) => {
                          const out = s.stock_quantity <= 0;
                          return (
                            <button
                              key={s.name}
                              type="button"
                              disabled={out}
                              onClick={() => addLine(selectedProduct, selectedVariant, s)}
                              className={`p-3 rounded-lg text-left border transition ${
                                out
                                  ? 'border-gray-200 opacity-40 cursor-not-allowed'
                                  : 'border-gray-300 hover:border-indigo-400 hover:bg-indigo-50'
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <span className="font-medium text-gray-900">{s.name}</span>
                                <span className={`text-xs px-2 py-0.5 rounded ${out ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                  {s.stock_quantity}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                {effectivePrice(selectedProduct, selectedVariant, s).toLocaleString()} MMK
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualOrderItemsEditor;
